import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import { app } from "../firebase";

class StripeConfigService {
  constructor() {
    this.db = getFirestore(app);
    this.cache = null;
    this.cacheExpiry = 0;
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
    this.listeners = new Set(); // For notifying components of changes
  }

  async getStripeMode() {
    try {
      // Check cache first
      if (this.cache && Date.now() < this.cacheExpiry) {
        return this.cache;
      }

      const configRef = doc(this.db, "admin", "stripeConfig");
      const configSnap = await getDoc(configRef);
      
      let mode = 'test'; // Default to test mode for safety
      
      if (configSnap.exists()) {
        const data = configSnap.data();
        mode = data.mode || 'test';
      } else {
        // Create default config if it doesn't exist
        await this.setStripeMode('test');
      }

      // Update cache
      this.cache = mode;
      this.cacheExpiry = Date.now() + this.CACHE_DURATION;
      
      return mode;
    } catch (error) {
      console.error("Error getting Stripe mode:", error);
      return 'test'; // Fallback to test mode on error
    }
  }

  async setStripeMode(mode) {
    try {
      if (!['test', 'live'].includes(mode)) {
        throw new Error('Invalid Stripe mode. Must be "test" or "live"');
      }

      const configRef = doc(this.db, "admin", "stripeConfig");
      await setDoc(configRef, { 
        mode,
        updatedAt: new Date(),
        updatedBy: 'admin'
      }, { merge: true });

      // Clear cache to force refresh
      this.cache = null;
      this.cacheExpiry = 0;
      
      // Notify all listeners of the change
      this.notifyListeners();
      
      console.log(`Stripe mode set to: ${mode}`);
      return true;
    } catch (error) {
      console.error("Error setting Stripe mode:", error);
      throw error;
    }
  }

  // Get the appropriate Stripe publishable key based on current mode
  async getPublishableKey() {
    const mode = await this.getStripeMode();
    
    const keys = {
      test: process.env.REACT_APP_STRIPE_PERSH_TEST_KEY,
      live: process.env.REACT_APP_STRIPE_PERSH_KEY
    };

    return keys[mode];
  }

  // Clear cache - useful for immediate updates
  clearCache() {
    this.cache = null;
    this.cacheExpiry = 0;
  }

  // Add listener for configuration changes
  addChangeListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback); // Return cleanup function
  }

  // Notify all listeners of configuration change
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in Stripe config change listener:', error);
      }
    });
  }
}

// Export singleton instance
export const stripeConfigService = new StripeConfigService();
export default stripeConfigService;